import React, { useEffect, useState } from "react";

import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";

import InputAdornment from "@mui/material/InputAdornment";

import {
    useGetAdminUserPerfiles,
} from "../../../hooks/AdministracionUsuarios/adminPerfiles/getAdminUserPerfiles";

import {
    useGetAdminUserPermisos,
} from "../../../hooks/AdministracionUserPermisos/getAdminUserPermisos";

import {
    useDeletePermisosPerfil,
} from "../../../hooks/AdministracionUsuarios/adminPerfiles/deletePermisosPerfil";

interface DeletePermisoPerfilProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => Promise<void> | void;
    perfil: any;
}

const DeletePermisoPerfil: React.FC<DeletePermisoPerfilProps> = ({
    open,
    onClose,
    onSuccess,
    perfil,
}) => {

    const {
        perfiles,
        obtenerPerfiles,
        loading: loadingPerfiles,
    } = useGetAdminUserPerfiles();

    const {
        permisos,
        obtenerPermisos,
        loading: loadingPermisos,
    } = useGetAdminUserPermisos();

    const {
        eliminarPermisoPerfil,
        loading,
    } = useDeletePermisosPerfil();

    const [idPerfil, setIdPerfil] = useState(0);

    const [busquedaPerfil, setBusquedaPerfil] = useState("");

    const [permisosSeleccionados, setPermisosSeleccionados] =
        useState<number[]>([]);

    const [permisosOriginales, setPermisosOriginales] =
        useState<number[]>([]);

    const [gruposAbiertos, setGruposAbiertos] =
        useState<Record<string, boolean>>({});

    const [loadingData, setLoadingData] = useState(true);

    const perfilFiltrado = perfiles.find((p: any) =>
        p.nombre
            .toUpperCase()
            .includes(busquedaPerfil.toUpperCase())
    );

    const permisosAgrupados = permisos.reduce(
        (acc: any, permiso: any) => {

            if (!acc[permiso.sufijo]) {
                acc[permiso.sufijo] = [];
            }

            acc[permiso.sufijo].push(permiso);

            return acc;

        },
        {}
    );

    useEffect(() => {

        if (!open) {
            return;
        }

        const cargar = async () => {

            setLoadingData(true);

            await Promise.all([
                obtenerPerfiles(),
                obtenerPermisos(),
            ]);

            if (perfil) {

                setIdPerfil(perfil.id);

                setBusquedaPerfil(perfil.nombre);

                const permisosPerfil =
                    perfil.permisos.map(
                        (permiso: any) => permiso.id
                    );

                setPermisosOriginales(
                    permisosPerfil
                );

                setPermisosSeleccionados(
                    permisosPerfil
                );

            }

            setLoadingData(false);

        };

        cargar();

    }, [open, perfil]);

    const toggleGrupo = (grupo: string) => {

        setGruposAbiertos((prev) => ({
            ...prev,
            [grupo]: !prev[grupo],
        }));

    };

    const togglePermiso = (id: number) => {

        if (!permisosOriginales.includes(id)) {
            return;
        }

        setPermisosSeleccionados((prev) =>

            prev.includes(id)
                ? prev.filter((x) => x !== id)
                : [...prev, id]

        );

    };

    const cerrarModal = () => {

        setIdPerfil(0);

        setBusquedaPerfil("");

        setPermisosSeleccionados([]);

        setPermisosOriginales([]);

        onClose();

    };

    const guardar = async () => {

        const permisosEliminar =
            permisosOriginales.filter(
                (id) =>
                    !permisosSeleccionados.includes(id)
            );

        if (permisosEliminar.length === 0) {

            cerrarModal();

            return;

        }

        for (const idPermiso of permisosEliminar) {

            // Cambiar posteriormente por idRelacion
            await eliminarPermisoPerfil(idPermiso);

        }

        await onSuccess?.();

        cerrarModal();

    };

    return (

        <Modal
            open={open}
            onClose={cerrarModal}
        >

            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "95%",
                    maxWidth: 1300,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                }}
            >

                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >

                    <Typography
                        variant="h6"
                        fontWeight="bold"
                    >
                        Quitar Permisos del Perfil
                    </Typography>

                    <IconButton
                        onClick={cerrarModal}
                    >
                        <CloseIcon />
                    </IconButton>

                </Box>

                <Divider sx={{ mb: 3 }} />

                {loadingData ? (

                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        py={8}
                        gap={2}
                    >

                        <CircularProgress />

                        <Typography
                            color="text.secondary"
                        >
                            Cargando información...
                        </Typography>

                    </Box>

                ) : (

                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={3}
                    >

                        <TextField
                            fullWidth
                            placeholder="Buscar perfil..."
                            value={busquedaPerfil}
                            onChange={(e) =>
                                setBusquedaPerfil(
                                    e.target.value
                                )
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {perfilFiltrado && (

                            <Typography
                                fontWeight={600}
                            >
                                Perfil:
                                {" "}
                                {perfilFiltrado.nombre}
                            </Typography>

                        )}

                        <TableContainer
                            component={Paper}
                            sx={{
                                maxHeight: 550,
                            }}
                        >

                            <Table>

                                <TableBody>

                                    {Object.entries(
                                        permisosAgrupados
                                    ).map(
                                        ([grupo, lista]: any) => (

                                            <React.Fragment
                                                key={grupo}
                                            >

                                                <TableRow
                                                    hover
                                                    onClick={() =>
                                                        toggleGrupo(
                                                            grupo
                                                        )
                                                    }
                                                    sx={{
                                                        cursor: "pointer",
                                                        bgcolor:
                                                            "#F5F5F5",
                                                    }}
                                                >

                                                    <TableCell
                                                        colSpan={2}
                                                        sx={{
                                                            fontWeight:
                                                                "bold",
                                                        }}
                                                    >

                                                        {gruposAbiertos[
                                                            grupo
                                                        ]
                                                            ? (
                                                                <KeyboardArrowDownIcon
                                                                    sx={{
                                                                        mr: 1,
                                                                    }}
                                                                />
                                                            )
                                                            : (
                                                                <KeyboardArrowRightIcon
                                                                    sx={{
                                                                        mr: 1,
                                                                    }}
                                                                />
                                                            )}

                                                        {grupo}
                                                        {" ("}
                                                        {lista.length}
                                                        {")"}

                                                    </TableCell>

                                                </TableRow>

                                                {gruposAbiertos[
                                                    grupo
                                                ] && (

                                                        <Box
                                                            sx={{
                                                                p: 2,
                                                                display:
                                                                    "grid",
                                                                gridTemplateColumns:
                                                                    "repeat(2,minmax(320px,1fr))",
                                                                columnGap: 6,
                                                                rowGap: .5,
                                                            }}
                                                        >

                                                            {lista.map(
                                                                (
                                                                    permiso: any
                                                                ) => (

                                                                    <Box
                                                                        key={
                                                                            permiso.id
                                                                        }
                                                                        sx={{
                                                                            display:
                                                                                "flex",
                                                                            alignItems:
                                                                                "center",
                                                                        }}
                                                                    >

                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={permisosSeleccionados.includes(
                                                                                permiso.id
                                                                            )}

                                                                            disabled={
                                                                                !permisosOriginales.includes(
                                                                                    permiso.id
                                                                                )
                                                                            }

                                                                            onChange={() =>
                                                                                togglePermiso(
                                                                                    permiso.id
                                                                                )
                                                                            }

                                                                            sx={{
                                                                                p: .3,
                                                                                mr: .5,
                                                                            }}
                                                                        />

                                                                        <Typography
                                                                            fontSize={
                                                                                13
                                                                            }
                                                                        >
                                                                            {
                                                                                permiso.nombre
                                                                            }
                                                                        </Typography>

                                                                    </Box>

                                                                )
                                                            )}

                                                        </Box>

                                                    )}

                                            </React.Fragment>

                                        )
                                    )}

                                </TableBody>

                            </Table>

                        </TableContainer>

                    </Box>

                )}

                <Divider
                    sx={{
                        mt: 4,
                        mb: 3,
                    }}
                />

                <Box
                    display="flex"
                    justifyContent="flex-end"
                    gap={2}
                >

                    <Button
                        variant="contained"
                        onClick={cerrarModal}
                        sx={{
                            bgcolor: "#F70083",
                            color: "#fff",
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: 2,
                            px: 3,
                            "&:hover": {
                                bgcolor: "#D60072",
                            },
                        }}
                    >
                        Cancelar
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        onClick={guardar}
                        disabled={loading}
                        sx={{
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: 2,
                            px: 3,
                        }}
                    >
                        {loading ? (

                            <>
                                <CircularProgress
                                    size={18}
                                    color="inherit"
                                    sx={{
                                        mr: 1,
                                    }}
                                />

                                Eliminando...

                            </>

                        ) : (

                            "Quitar Permisos"

                        )}
                    </Button>

                </Box>

            </Box>

        </Modal>

    );

};

export default DeletePermisoPerfil;